import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SidebarLayout from '@/components/layout/SidebarLayout';
import FilterSection from '@/components/image-generator/FilterSection/FilterSection';
import { PrivateImageLibrary } from '@/components/private-image-generator/PrivateImageLibrary';
import { useToast } from '@/hooks/errors/use-toast';
import { ImageIcon } from 'lucide-react';

// Import the existing public image filters hook (shared infrastructure)
import { useImageFilters } from '@/hooks/image-generator/image-filters/use-image-filters';
import { usePrivateImageGeneration } from '@/hooks/private-image-generator/usePrivateImageGeneration';
import { usePrivateImageLibraryData } from '@/hooks/private-image-generator/usePrivateImageLibrary';

// Import the new universal empty state system
import { UniversalEmptyStateWrapper, EmptyStateLayout, getAppConfig, PrivateImageForm } from '@/components/shared/empty-states';

// Import quest detection hook
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';

const PrivateImageGenerator = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenImageId, setAutoOpenImageId] = useState<string | null>(null);
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // Import hooks for detecting if user has images
  const { images: allImages, isLoading: isImagesLoading } = usePrivateImageLibraryData({});
  
  // Check for openImage URL parameter and set up auto-open
  useEffect(() => {
    console.log('[PrivateImageGenerator] Checking URL parameters:', {
      hasSearchParams: !!searchParams,
      searchParamsString: searchParams.toString(),
      openImageParam: searchParams.get('openImage')
    });
    
    const openImageId = searchParams.get('openImage');
    if (openImageId) {
      console.log('[PrivateImageGenerator] Auto-opening image from URL parameter:', openImageId);
      
      // Store the image ID to pass to PrivateImageLibrary
      setAutoOpenImageId(openImageId);
      
      // Keep the URL parameter for reliability and better UX
      // Users can bookmark/share the URL with the specific image
    }
  }, [searchParams]);

  // Get app configuration
  const appConfig = getAppConfig('private-image-generator');

  // Get image filters state and handlers using the standard hook
  const {
    selectedType,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    width,
    height,
    setWidth,
    setHeight,
    imageDescription,
    setImageDescription,
    debouncedImageDescription,
    isAdvancedMode,
    setIsAdvancedMode
  } = useImageFilters();
  
  // Function to handle width changes
  const handleWidthChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setWidth(value);
    }
  };

  // Function to handle height changes
  const handleHeightChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setHeight(value);
    }
  };
  
  // Handle image description changes
  const handleImageDescriptionChange = (value: string) => {
    setImageDescription(value);
  };

  // Project selection state for generation
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Use the private image generation hook
  const {
    isGenerating,
    isImageLoading,
    selectedImage,
    setSelectedImage,
    handleGenerateImage
  } = usePrivateImageGeneration({
    imageDescription,
    width,
    height,
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions,
    selectedProjectId
  });

  // Memoize the sidebar content to prevent re-initialization during layout changes
  const sidebarContent = useMemo(() => (
    <FilterSection
      selectedType={selectedType}
      onTypeChange={handleTypeChange}
      styleOptions={styleOptions}
      selectedStyle={selectedStyle || ''}
      onStyleChange={handleStyleChange}
      setSelectedStyle={setSelectedStyle}
      influenceOptions={influenceOptions}
      selectedInfluence={selectedInfluence || ''}
      onInfluenceChange={handleInfluenceChange}
      setSelectedInfluence={setSelectedInfluence}
      colorOptions={colorOptions}
      onColorChange={handleColorChange}
      backgroundOptions={backgroundOptions}
      onBackgroundChange={handleBackgroundChange}
      selectedBackground={selectedBackground || ''}
      environmentOptions={environmentOptions}
      onEnvironmentChange={handleEnvironmentChange}
      moodOptions={moodOptions}
      onMoodChange={handleMoodChange}
      imageDescription={imageDescription}
      onImageDescriptionChange={handleImageDescriptionChange}
      width={width}
      height={height}
      onWidthChange={handleWidthChange}
      onHeightChange={handleHeightChange}
      isGenerating={isGenerating}
      isImageLoading={isImageLoading}
      onGenerate={handleGenerateImage}
      isAdvancedMode={isAdvancedMode}
      setIsAdvancedMode={setIsAdvancedMode}
      selectedProjectId={selectedProjectId}
      onProjectChange={setSelectedProjectId}
    />
  ), [
    selectedType,
    handleTypeChange,
    styleOptions,
    selectedStyle,
    handleStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    handleInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    handleColorChange,
    backgroundOptions,
    handleBackgroundChange,
    selectedBackground,
    environmentOptions,
    handleEnvironmentChange,
    moodOptions,
    handleMoodChange,
    imageDescription,
    handleImageDescriptionChange,
    width,
    height,
    handleWidthChange,
    handleHeightChange,
    isGenerating,
    isImageLoading,
    handleGenerateImage,
    isAdvancedMode,
    setIsAdvancedMode,
    selectedProjectId,
    setSelectedProjectId
  ]);

  // Empty state content
  const emptyStateContent = (
    <EmptyStateLayout
      icon={<appConfig.icon className={`h-8 w-8 ${appConfig.iconColor}`} />}
      title={appConfig.emptyStateTitle}
      variant={appConfig.variant}
    >
      <PrivateImageForm 
        onImageCreated={() => {
          // Image creation will automatically refresh the image list
          console.log('Private image created, layout will update automatically');
        }}
      />
    </EmptyStateLayout>
  );

  // Normal content when images exist
  const normalContent = (
    <PrivateImageLibrary 
      styleId={selectedStyle}
      influenceId={selectedInfluence}
      backgroundId={selectedBackground}
      environmentIds={environmentOptions.filter(o => o.checked).map(o => o.id)}
      moodIds={moodOptions.filter(o => o.checked).map(o => o.id)}
      colorIds={colorOptions.filter(o => o.checked).map(o => o.id)}
      imageType={selectedType}
      description={imageDescription}
      debouncedDescription={debouncedImageDescription}
      width={width}
      height={height}
      selectedImage={selectedImage}
      onImageSelect={setSelectedImage}
      projectId={selectedProjectId}
      autoOpenImageId={autoOpenImageId}
      onAutoOpenComplete={() => setAutoOpenImageId(null)}
    />
  );

  return (
    <UniversalEmptyStateWrapper
      isLoading={isImagesLoading}
      hasData={allImages.length > 0}
      title={appConfig.title}
      description={appConfig.description}
      icon={<appConfig.icon size={24} />}
      sidebar={sidebarContent}
      emptyStateContent={emptyStateContent}
    >
      {normalContent}
    </UniversalEmptyStateWrapper>
  );
};

export default PrivateImageGenerator; 